import { identityTransition$ } from "../session/atom";
import { writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { flushSync } from "react-dom";
import { settlePendingWrites } from "./pending-writes";

/**
 * How many crossings are holding the app in its identity-transition state.
 *
 * A depth rather than a flag because crossings overlap: a cross-tab reconcile can land while an
 * interactive impersonation is still in flight, and whichever finishes first must not lift the state
 * the other is still relying on.
 */
let held = 0;

/**
 * Take the app across an identity boundary: settle what the outgoing identity is still holding, then
 * run `crossing` with every page unmounted.
 *
 * Both halves, in this order, and neither is optional:
 *
 * - **Settle first, while the pages are still mounted.** A writer registers its flush from an effect
 *   (`usePersistColState`), so unmounting takes that flush out of the registry with it. Settled here,
 *   a layout still sitting on its debounce is handed over and sent under the identity that arranged
 *   it. See `./pending-writes.ts`.
 * - **Then hold `identityTransition$`,** which is what `PlatformRouter` renders a loader for. That
 *   closes the write the settle cannot reach: a grid left mounted across the crossing re-renders
 *   against the arriving identity's store — its classification columns gone — and persists *that* as
 *   a new layout, under whoever has arrived. Nothing mounted, nothing to persist.
 *
 * The hold is committed with `flushSync` before `crossing` is entered, because `crossing`'s own first
 * statement is usually the one that moves the identity, and a render React has merely been asked for
 * is no barrier against it.
 *
 * Not for a credential *renew*: the identity is unchanged there, and blanking the app behind a loader
 * on every silent refresh — in every open tab — buys nothing. `cross-tab-session` draws that line.
 */
export async function crossIdentityBoundary<T>(
	crossing: () => Promise<T>,
	opts?: {
		/**
		 * Whether the outgoing identity's writes can still be sent. `false` where its credential is
		 * already gone — an expired session, one that never finished hydrating — because nothing is
		 * left to authenticate them with: they are given up rather than waited for, and waiting would
		 * buy a delayed teardown and a row of 401s.
		 *
		 * The hold still applies. A crossing with no credential is still a crossing, and a page left
		 * mounted through it is a page writing per-user state as nobody.
		 */
		settleOutgoing?: boolean;
	},
): Promise<T> {
	if (opts?.settleOutgoing ?? true) {
		await settlePendingWrites();
	}
	if (held === 0) {
		flushSync(() => writeAtom(identityTransition$, true));
	}
	held += 1;
	try {
		return await crossing();
	} finally {
		held -= 1;
		if (held === 0) {
			writeAtom(identityTransition$, false);
		}
	}
}
